import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { PlaceOutlined } from "@mui/icons-material";
import { apiGetRequest, apiPostRequest } from "../../Helpers/Requests";
import endpoints from "../../Helpers/Endpoints";
import { useSnackbar } from "../SnackBar";
import CustomTooltip from "../CustomTooltip";
import {
  cardStyles,
  imageStyles,
  cardContentStyles,
  nameStyles,
  managerStyles,
  communityInfoBoxStyles,
  communityInfoTextStyles,
  locationTextStyles,
  tagsTextStyles,
  joinButtonStyles,
  cancelButtonStyles,
  communityNameBoxStyles,
} from "./styles/styles";
import PointerUtils from "../../Utils/pointer";
import SupplierJoinOffersModal from "../Modals/SupplierJoinOffersModal";
import CancelOfferModel from "../Modals/CancelOfferModel";
import { useTranslation } from "react-i18next";
import NeedPricelistModal from "../Modals/NeedPricelistModal";
import { useNavigate } from "react-router-dom";
// import { previewBadge } from "../../Layouts/CommunityLayout/styles/styles";

const CommunityCard = (props) => {
  const { communityData, initialRequestState = false } = props;
  const {
    managers = [],
    image_url = "",
    name = "",
    location = "",
    tags = [],
    member_count = 0,
    id,
  } = communityData || {};
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();
  const [requestForJoin, setRequestForJoin] = useState(initialRequestState);
  const [openJoinModel, setOpenJoinModel] = useState(false);
  const [openNeedPricelistModal, setOpenNeedPricelistModal] = useState(false);
  const [openCancelModel, setOpenCancelModel] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [actionButtonLoader, setActionButtonLoader] = useState(false);

  // Update requestForJoin state if initialRequestState prop changes
  // This helps when the component updates with new data from the parent
  useEffect(() => {
    setRequestForJoin(initialRequestState);
  }, [initialRequestState]);

  const { t } = useTranslation(["community-card", "bottom-menu-and-settings"]);

  const handleJoinCommunity = async () => {
    try {
      setActionButtonLoader(true);
      // A supplier can join with either a services pricelist OR products — only
      // block when they have neither.
      const [pricelistRes, productsRes] = await Promise.all([
        apiGetRequest(endpoints.supplierPricelists),
        apiGetRequest(endpoints.supplierProducts),
      ]);
      // Filter out draft pricelists (is_draft === true)
      const validPricelists = (pricelistRes?.data?.pricelists || []).filter(
        (pl) => !pl.is_draft
      );
      const activeProducts = (productsRes?.data?.products || []).filter(
        (p) => p.is_active
      );
      if (!validPricelists?.length && !activeProducts?.length) {
        setOpenNeedPricelistModal(true);
        return;
      }

      setOpenJoinModel(true);
    } catch (error) {
      console.log("error occurred while checking pricelists ", error);
    } finally {
      setActionButtonLoader(false);
    }
  };

  const handleCancelJoinCommunity = () => {
    setOpenCancelModel(true);
  };

  // const handleJoinRequest = async (discount = 0) => {
  //   try {
  //     setIsLoading(true);
  //     PointerUtils.disable();
  //     const pricelistRes = await apiGetRequest(endpoints.supplierPricelists);
  //     const hasAnyPricelist = pricelistRes?.data?.pricelists?.length > 0;

  //     if (!hasAnyPricelist) {
  //       setOpenNeedPricelistModal(true);
  //       return;
  //     }

  //     const response = await apiPostRequest(
  //       endpoints.joinCommunityRequest(id),
  //       {
  //         supplier_discount: Number(discount),
  //       }
  //     );
  //     if (response.status === 200) {
  //       showSnackbar(`Join request has been sent for ${name}`, "success");
  //       setRequestForJoin(true);
  //       setOpenJoinModel(false);
  //     }
  //   } catch (error) {
  //     showSnackbar(`Failed to send join request for ${name}`, "error");
  //     console.log("error occurred while sending join request ", error);
  //   } finally {
  //     setIsLoading(false);
  //     PointerUtils.enable();
  //   }
  // };

  const handleCancelRequest = async () => {
    try {
      setIsLoading(true);
      PointerUtils.disable();
      const response = await apiPostRequest(
        endpoints.cancelJoinCommunityRequest(id),
        {
          feedback: "",
        }
      );
      if (response.status === 200) {
        showSnackbar(t("join_request_cancelled") + " " + name, "success");
        setRequestForJoin(false);
        setOpenCancelModel(false);
      }
    } catch (error) {
      showSnackbar(t("join_request_cancel_fail") + " " + name, "error");
      console.log("error occurred while canceling the join request ", error);
    } finally {
      setIsLoading(false);
      PointerUtils.enable();
    }
  };

  // If communityData is not defined properly, don't render the card
  if (!communityData || !id) {
    return null;
  }

  const handleActionClick = () => {
    setRequestForJoin(true);
  };

  const closeNeedPricelistModal = () => {
    setOpenNeedPricelistModal(false);
  };

  const handleNeedPricelistActionClick = () => {
    navigate("/manage-pricelists");
  };

  const cancelOfferTitle = t("offer_modal.cancel_offer_title");
  const cancelOfferText =
    t("offer_modal.cancel_invite_text") + " " + name + "?";

  return (
    <>
      {/* <JoinCommunityModel
        {...{
          open: openJoinModel,
          communityData,
          handleClose: () => setOpenJoinModel(false),
          handleJoinRequest: (discount) => handleJoinRequest(discount),
          isLoading,
        }}
      /> */}

      <SupplierJoinOffersModal
        {...{
          open: openJoinModel,
          onClose: () => setOpenJoinModel(false),
          onActionClick: handleActionClick,
          communityData,
          viewType: "join",
          sendJoinRequest: true,
        }}
      />

      <NeedPricelistModal
        open={openNeedPricelistModal}
        onClose={closeNeedPricelistModal}
        onActionClick={handleNeedPricelistActionClick}
      />

      {openCancelModel && (
        <CancelOfferModel
          {...{
            open: openCancelModel,
            communityData,
            handleClose: () => setOpenCancelModel(false),
            handleCancelRequest: () => handleCancelRequest(),
            dialogTitle: cancelOfferTitle,
            dialogHeading: cancelOfferText,
            isLoading: isLoading,
          }}
        />
      )}

      <Card elevation={0} sx={cardStyles}>
        <Box component="img" src={image_url} alt="Community" sx={imageStyles} />

        <CardContent sx={cardContentStyles}>
          <Box>
            <Box sx={communityNameBoxStyles}>
              <Typography sx={nameStyles}>{name}</Typography>

              {/* preview badge for phase 1 */}
              {/* <Box component="span" sx={previewBadge}>
                {t("bottom-menu-and-settings:preview")}
              </Box> */}
            </Box>
            <Typography sx={managerStyles}>
              {managers && managers.length > 0
                ? managers[0].name
                : t("community_manager")}
            </Typography>

            <Box sx={communityInfoBoxStyles}>
              <Box>
                <img src="/members_icon.svg" alt="members icon" />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography sx={communityInfoTextStyles}>
                  {t("community_members")}: {member_count.toLocaleString()}
                </Typography>
              </Box>
            </Box>

            <Box sx={communityInfoBoxStyles}>
              <Box>
                <PlaceOutlined sx={{ fontSize: 16, color: "#2F2B3D" }} />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography sx={communityInfoTextStyles}>
                  {t("community_location")}
                </Typography>
                {location ? (
                  <CustomTooltip title={location}>
                    <Typography sx={locationTextStyles}>{location}</Typography>
                  </CustomTooltip>
                ) : (
                  <Typography sx={locationTextStyles}>-</Typography>
                )}
              </Box>
            </Box>

            <Box sx={communityInfoBoxStyles}>
              <Box>
                <img src="/grid_view_icon.svg" alt="grid view" />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography sx={communityInfoTextStyles}>
                  {t("community_tags")}
                </Typography>
                {tags.length > 0 ? (
                  <CustomTooltip
                    title={tags.map((tag) => tag.name).join(" | ")}
                  >
                    <Typography sx={tagsTextStyles}>
                      {tags.map((tag, index) => (
                        <React.Fragment key={tag.id || index}>
                          {index > 0 && " | "}
                          {tag.name}
                        </React.Fragment>
                      ))}
                    </Typography>
                  </CustomTooltip>
                ) : (
                  <Typography sx={tagsTextStyles}>-</Typography>
                )}
              </Box>
            </Box>
          </Box>

          {!requestForJoin ? (
            <Button
              onClick={handleJoinCommunity}
              variant="contained"
              fullWidth
              disabled={actionButtonLoader}
              // disabled={true} // action button disabled for phase 1
              sx={joinButtonStyles}
            >
              {t("join_community")}{" "}
              {actionButtonLoader && (
                <CircularProgress
                  size={20}
                  color="rgba(255, 255, 255, 1)"
                  sx={{ ml: "8px" }}
                />
              )}
            </Button>
          ) : (
            <Button
              onClick={handleCancelJoinCommunity}
              variant="contained"
              fullWidth
              sx={cancelButtonStyles}
            >
              {t("cancel_join_request")}
            </Button>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default CommunityCard;
